import * as React from 'react';
import styled, {css} from 'styled-components';
import {
  useTable,
  Column,
  Row,
  ColumnInstance,
  HeaderGroup, Cell,
} from 'react-table';
import {transparentize} from 'polished';

export enum TableVariant {
  Default = 'default',
  Accent = 'accent'
}

export interface TableProps<D extends object = {}> {
  variant?: TableVariant;
  columns: Column<D>[];
  data: D[];
}

const Container = styled.table<Pick<TableProps, 'variant'>>`
  width: 100%;
  border-collapse: collapse;
  box-sizing: border-box;
  text-align: left;

  border-left: 1px solid ${({theme}) => theme.variables.tableBorderColor};
  border-right: 1px solid ${({theme}) => theme.variables.tableBorderColor};

  ${({variant, theme}) => {
    switch (variant) {
      case TableVariant.Default: return css`
        background: ${theme.variables.accentBackgroundColor};
      `;
      case TableVariant.Accent: return css`
        background: ${theme.variables.backgroundColor};
      `;
    }
  }};
`;

const Header = styled.thead<Pick<TableProps, 'variant'>>`
  border-bottom: 1px solid ${({theme}) => theme.variables.tableBorderColor};
  ${({variant, theme}) => {
    switch (variant) {
      case TableVariant.Default: return css`
        background: ${transparentize(0.98, theme.colors.white)};
      `;
    }
  }};
`;

const HeaderCell = styled.th<Pick<TableProps, 'variant'>>`
  padding: 10px 20px;

  font-size: 13px;
  font-weight: 400;
  line-height: 16px;

  color: ${({theme}) => transparentize(.6, theme.colors.white)};
  border-top: 1px solid ${({theme}) => theme.variables.tableBorderColor};
`;

const Body = styled.tbody`

`;

const TableRow = styled.tr`

`;

const TableCell = styled.td<Pick<TableProps, 'variant'>>`
  padding: 12px 20px;

  font-size: 14px;
  font-weight: 500;
  line-height: 16px;

  border-bottom: 1px solid ${({theme}) => theme.variables.tableBorderColor};
`;

/**
 * The component render table
 * @param {TableProps} props
 * @return {React.ReactElement<TableProps>}
 * @constructor
 */
function Table<D extends object = {}>(
    props: TableProps<D>,
): React.ReactElement<TableProps<D>> {
  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
  } = useTable<D>({
    columns: props.columns,
    data: props.data,
  });

  /**
   * Render header part
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    /**
     * Render header group
     * @param {HeaderGroup<D>} group
     * @return {JSX.Element}
     */
    function renderHeaderGroup(group: HeaderGroup<D>) {
      const headerGroupProps = group.getHeaderGroupProps();
      return (
        <TableRow {...headerGroupProps}>
          {
            group
                .headers
                .map(renderHeaderColumn)
          }
        </TableRow>
      );
    }

    /**
     * Render header column
     * @param {ColumnInstance<D>} column
     * @return {JSX.Element}
     */
    function renderHeaderColumn(column: ColumnInstance<D>) {
      const headerProps = column.getHeaderProps();
      return (
        <HeaderCell {...headerProps} variant={props.variant}>
          {
            column.render('Header')
          }
        </HeaderCell>
      );
    }

    return (
      <Header variant={props.variant}>
        {
          headerGroups.map(renderHeaderGroup)
        }
      </Header>
    );
  }

  /**
   * Render body part
   * @return {JSX.Element}
   */
  function renderBody(): JSX.Element {
    /**
     * Render table row
     * @param {Row} row
     * @return {JSX.Element}
     */
    function renderRow(row: Row<D>) {
      prepareRow(row);
      return (
        <TableRow {...row.getRowProps()}>
          {
            row
                .cells
                .map(renderCell)
          }
        </TableRow>
      );
    }

    /**
     * Render table cell
     * @param {Cell} cell
     * @return {JSX.Element}
     */
    function renderCell(cell: Cell<D>) {
      const cellProps = cell.getCellProps();
      return (
        <TableCell {...cellProps} variant={props.variant}>
          {
            cell.render('Cell')
          }
        </TableCell>
      );
    }

    return (
      <Body {...getTableBodyProps()}>
        {
          rows.map(renderRow)
        }
      </Body>
    );
  }

  return (
    <Container {...getTableProps()} variant={props.variant}>
      {renderHeader()}
      {renderBody()}
    </Container>
  );
}

Table.defaultProps = {
  variant: TableVariant.Default,
};

export default Table;
